import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { addDays, deepCopy, getDataScopeByPermission, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { FreightSearchService } from '../../services/freight-search.service';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { FacilityService } from 'src/app/core/services/facility.service';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { LaasOption } from 'src/app/core/models/laas-option';
import { DateTableColumn } from 'src/app/core/models/date-table-column';
import { FreightSearchResult } from 'src/app/core/models/transport-operation/freight-search-result';
import { endOfDay, startOfDay } from 'date-fns';
import { finalize } from 'rxjs/operators';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { PageInfo } from 'src/app/core/models/page-info';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';

@Component({
  selector: 'laas-freight-search',
  templateUrl: './freight-search.component.html',
  styleUrls: ['./freight-search.component.scss']
})
export class FreightSearchComponent extends SessionBaseComponent implements OnInit {

  readonly permissionCode: any = PermissionConstants.ViewFreightSearch;
  readonly preferenceKey: string = UserPreferenceConstants.FreightSearch;
  readonly dayRange: number = 90;
  readonly now: Date = new Date();

  currentLang: string = this.laasSetting.language;
  facilityOptions: LaasOption[] = [];
  shipCorpOptions: LaasOption[] = [];
  vendorOrConsigneeOptions: any[] = [];
  brandOptions: LaasOption[] = [];

  defaultCreateDt: Date[];

  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };

  sortOrder: SortOrder = {
    property: "createDt",
    direction: -1
  };

  searchForm: FormGroup;
  tableColumns: DateTableColumn[];
  searchResults: FreightSearchResult[] = [];

  isSearchFormOpen: boolean = true;
  isLoading: boolean = false;

  @ViewChild('createDt') createDt!: CalendarComponent;
  @ViewChild('dataTable') dataTable!: DataTableComponent;

  constructor(
    injector: Injector,
    private facilityService: FacilityService,
    private freightSearchService: FreightSearchService,
    private dropdownOptionsService: DropdownOptionsService,
    private fb: FormBuilder,
  ) {
    super(injector);

    this.defaultCreateDt = [startOfDay(addDays(new Date(), this.dayRange * -1)), endOfDay(new Date())];

    this.searchForm = this.fb.group({
      dataScopes: [],
      facilityList: [],
      shipCorpList: [],
      vendorOrConsigneeList: [],
      expressBillList: [],
      brandList: [],
      adNoDoNoList: [],
      createDtStart: this.defaultCreateDt[0],
      createDtEnd: this.defaultCreateDt[1],
    });

    this.tableColumns = [
      { field: 'adNo', header: 'FreightSearch.MainTable.AdNo', sort: true },
      { field: 'doNo', header: 'FreightSearch.MainTable.DoNo', sort: true },
      { field: 'department', header: 'FreightSearch.MainTable.Department', sort: true },
      { field: 'shipCorp', header: 'FreightSearch.MainTable.ShipCorp', sort: true },
      { field: 'createDt', header: 'FreightSearch.MainTable.BillingCreateDt', sort: true },
      { field: 'vendor', header: 'FreightSearch.MainTable.Vendor', sort: true },
      { field: 'brand', header: 'FreightSearch.MainTable.Brand', sort: true },
      { field: 'preCurrencyCode', header: 'FreightSearch.MainTable.PreCurrencyCode', sort: true },
      { field: 'forwarder', header: 'FreightSearch.MainTable.Forwarder', sort: true },
      { field: 'estimatedFreightChargeAmount', header: 'FreightSearch.MainTable.EstimatedFreightChargeAmount', sort: true },
      { field: 'freightChargeAmount', header: 'FreightSearch.MainTable.FreightChargeAmount', sort: true },
      { field: 'estimatedFuelSurchargeAmount', header: 'FreightSearch.MainTable.EstimatedFuelSurchargeAmount', sort: true },
      { field: 'fuelSurchargeAmount', header: 'FreightSearch.MainTable.FuelSurchargeAmount', sort: true },
      { field: 'estimatedSecuritySurchargeAmount', header: 'FreightSearch.MainTable.EstimatedSecuritySurchargeAmount', sort: true },
      { field: 'securitySurchargeAmount', header: 'FreightSearch.MainTable.SecuritySurchargeAmount', sort: true },
      { field: 'estimatedOtherChargeAmount', header: 'FreightSearch.MainTable.EstimatedOtherChargeAmount', sort: true },
      { field: 'otherChargeAmount', header: 'FreightSearch.MainTable.OtherChargeAmount', sort: true },
      { field: 'expressBill', header: 'FreightSearch.MainTable.ExpressBill', sort: true }
    ];
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.dropdownOptionsService.getFacilityOptions().subscribe({
      next: (result) => {
        this.facilityOptions = result.map(
          (x: { optionDesc: string; optionCode: string }) => ({
            label: x.optionDesc,
            value: x.optionCode,
          })
        );
      },
    });

    this.facilityService.getTenantOrgOptions().subscribe({
      next: (options: any[]) => {
        this.shipCorpOptions = options.map(x => ({ label: `${x.orgId} - ${x.orgName}`, value: x.orgId}));
      }
    });

    this.dropdownOptionsService.getBrandOptions(1).subscribe({
      next: (options: any[]) => {
        this.brandOptions = options.map(x => ({ label: x.brandName, value: x.brandName }));
      }
    });

    this.facilityService.getAllVendorOrConsigneeOptions().subscribe({
      next: (result) => {
        this.vendorOrConsigneeOptions = [...result].map((x: any) => ({
          label: `${x.vendorNo} - ${x.vendorName}`,
          value: x.vendorNo + "#@" + x.vendorName
        }));
      },
    });
  }

  tableSort(event: any): void {
    this.sortOrder = { property: event.field, direction: event.order };
    this.dataTable.setCurrentPage(0);
    this.searchClickBtn();
  }

  tablePageChange(event: any) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.searchClickBtn();
  }

  searchClickBtn(): void {
    this.requestOfQuery();
  }

  requestOfQuery(): void {

    if (!this.checkSearchParamsVaildity()) {
      return;
    }

    const params = deepCopy(this.searchForm.value);

    params.pageIndex = this.pageInfo.pageIndex;
    params.pageSize = this.pageInfo.pageSize;
    params.sortInfo = this.sortOrder;
    params.dataScopes = getDataScopeByPermission(this.userProfile, this.permissionCode);

    this.isLoading = true;

    this.freightSearchService.searchFreight(params)
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe(
        (result) => {
          this.isSearchFormOpen = false;
          this.searchResults = deepCopy(result.content) ?? [];
          this.pageInfo.totalRecords = result.totalElements;
          this.pageInfo.totalPages = this.pageInfo.totalRecords / this.pageInfo.pageSize;
          this.dataTable.setPageInfo(this.pageInfo.totalRecords);
        },
        (err: any) => {
          this.pageInfo.totalRecords = 0;
          this.pageInfo.totalPages = 0;
          this.dataTable.setPageInfo(this.pageInfo.totalRecords);
          this.toastErrorMessage(err.error);
        }
      );
  }

  exportClickBtn(event: any): void {
    if (!this.checkSearchParamsVaildity()) {
      return;
    }

    const params = deepCopy(this.searchForm.value);
    params.dataScopes = getDataScopeByPermission(this.userProfile, this.permissionCode);

    this.isLoading = true;

    this.freightSearchService.getFreightExcel(params)
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe(
        (result) => {
          if (result == null || result.size === 0) {
            this.toastService.info(
              this.translateService.instant('BillSearch.Alert.NoFound')
            );
            this.loader.hide();
            return;
          }

          const file = result;
          file.name = 'Freight';
          const url = URL.createObjectURL(file);
          const a: any = document.createElement('a');
          a.href = url;
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(url);
        },
        (err: any) => {
          this.toastErrorMessage(err.error);
        }
      );
  }

  checkSearchParamsVaildity(): boolean {
    const expressBillList = this.searchForm.get('expressBillList')?.value;
    const expressBillListIsNotEmpty = !isNullOrEmpty(expressBillList) && expressBillList.length > 0;

    const adNoDoNoList = this.searchForm.get('adNoDoNoList')?.value;
    const adNoDoNoListIsNotEmpty = !isNullOrEmpty(adNoDoNoList) && adNoDoNoList.length > 0;

    if (expressBillListIsNotEmpty || adNoDoNoListIsNotEmpty) {
      return true;
    }

    const createDtStart = this.searchForm.get('createDtStart')?.value;
    const createDtEnd = this.searchForm.get('createDtEnd')?.value;

    if (isNullOrEmpty(createDtStart) && isNullOrEmpty(createDtEnd)) {
      this.toastService.warn(this.translateService.instant('FreightSearch.Error.CheckRequired'), false);
      return false;
    }

    if (createDtStart > createDtEnd) {
      this.toastService.warn(this.translateService.instant('FreightSearch.Error.CheckIfStartTimeGreaterThanEndTime'), false);
      return false;
    }

    if (createDtStart.addDays(this.dayRange).setHours(23, 59, 59, 999) < createDtEnd) {
      this.toastService.warn(this.translateService.instant('FreightSearch.Error.CheckIfStartTimeExceedsEndTimeRnageDays', { dayRange: this.dayRange }), false);
      return false;
    }

    return true;
  }

  dateOnChange(date: any, type: string) {
    switch (type) {
      case 'createDt':
        this.searchForm.controls.createDtStart.setValue(date.startDate);
        this.searchForm.controls.createDtEnd.setValue(date.endDate);
        break;
    }
  }

  chipsAdd(event: any, fromControlName: any): void {
    const value: string = event.value;
    const parsedValues: string[] = value.split('\r\n').filter(x => isNullOrEmpty(x) === false && x !== '\r\n');
    const formControl = this.searchForm.get(fromControlName);
    formControl?.value.splice(formControl?.value.indexOf(value));
    formControl?.setValue(formControl?.value.concat(parsedValues));
  }

  resetClickBtn(): void {
    this.createDt.ngOnInit();
    this.searchForm.reset({
      createDtStart: this.defaultCreateDt[0],
      createDtEnd: this.defaultCreateDt[1]
    });
  }
}
